import React, { useState, useEffect, useCallback } from 'react';
import ChatInput from './ChatInput';
import RecipePreview from './RecipePreview';
import { parseRecipe, refineRecipe } from '../../api/actions';
import './RecipeCreatorChat.css';

export default function RecipeCreatorChat({ onNavigateToBrowser }) {
    const [currentRecipe, setCurrentRecipe] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [elapsedTime, setElapsedTime] = useState(0);
    const abortControllerRef = React.useRef(null);

    // Warn before leaving with unsaved recipe
    useEffect(() => {
        const handler = (e) => {
            if (currentRecipe && !saved) {
                e.preventDefault();
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [currentRecipe, saved]);

    // Update elapsed time while loading
    useEffect(() => {
        if (!loading) return;

        const interval = setInterval(() => {
            setElapsedTime(prev => prev + 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [loading]);

    const handleSend = useCallback(async (text) => {
        setLoading(true);
        setElapsedTime(0);
        setStatusMessage(currentRecipe
            ? 'Refining recipe...'
            : 'Parsing recipe (this may take up to 2 minutes)...');

        // Create a new AbortController for this request
        abortControllerRef.current = new AbortController();

        try {
            const signal = abortControllerRef.current.signal;
            let recipe;
            if (!currentRecipe) {
                recipe = await parseRecipe(text, { signal });
                const stepCount = recipe.steps ? recipe.steps.length : 0;
                setStatusMessage(`Created "${recipe.name}" with ${stepCount} step${stepCount !== 1 ? 's' : ''}.`);
            } else {
                recipe = await refineRecipe(currentRecipe, text, { signal });
                const stepCount = recipe.steps ? recipe.steps.length : 0;
                setStatusMessage(`Updated "${recipe.name}" — ${stepCount} step${stepCount !== 1 ? 's' : ''}.`);
            }
            setCurrentRecipe(recipe);
            setSaved(false);
        } catch (err) {
            if (err.name === 'AbortError') {
                setStatusMessage('Recipe creation cancelled.');
            } else {
                const errorMsg = err.message || 'Unknown error';
                setStatusMessage(`Error: ${errorMsg}`);
            }
        } finally {
            setLoading(false);
            abortControllerRef.current = null;
        }
    }, [currentRecipe]);

    const handleCancel = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            setLoading(false);
            setStatusMessage('Cancelling recipe creation...');
        }
    }, []);

    const handleSaved = useCallback(() => {
        setSaved(true);
        setStatusMessage('Recipe saved successfully!');
    }, []);

    return (
        <div className="recipe-creator-chat">
            <RecipePreview
                recipe={currentRecipe}
                onSaved={handleSaved}
                onNavigateToBrowser={onNavigateToBrowser}
            />
            <div className="creator-bottom-bar">
                {statusMessage && (
                    <div className="creator-status">{statusMessage}</div>
                )}
                {loading && (
                    <div className="creator-status creator-loading">
                        <div className="loading-spinner"></div>
                        <span>Processing... {Math.floor(elapsedTime / 60)}:{String(elapsedTime % 60).padStart(2, '0')}</span>
                        <button
                            onClick={handleCancel}
                            className="cancel-btn"
                            title="Cancel recipe creation"
                        >
                            ✕
                        </button>
                    </div>
                )}
                <ChatInput
                    onSend={handleSend}
                    disabled={loading}
                    placeholder={currentRecipe
                        ? "Describe changes to refine the recipe..."
                        : "Paste a recipe or describe what you want to cook..."
                    }
                />
            </div>
        </div>
    );
}
